import React, { useEffect, useMemo, useState } from "react";
import { errorDev } from '../../utils/logger.js';

function getInitials(name, email) {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const fromWords = words
    .slice(0, 2)
    .map((word) => word[0])
    .join("");

  const fallbackSource = String(name || email || "").replace(/[^a-zA-Z]/g, "");
  const fallback = fallbackSource.slice(0, 2);

  return (fromWords || fallback || "U").slice(0, 2).toUpperCase();
}

export default function ProfileAvatar({
  avatarUrl,
  displayName,
  email,
  alt,
  size = "sm",
  className = "",
}) {
  const isLarge = size === "lg";
  const dimensions = isLarge ? "w-12 h-12 text-sm" : "w-8 h-8 text-xs";
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const normalizedAvatarUrl = useMemo(() => {
    const candidate = String(avatarUrl || "").trim();
    if (!candidate) return "";
    if (candidate.startsWith("data:") || candidate.startsWith("blob:")) return candidate;

    try {
      const parsed = new URL(candidate);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") {
        return candidate;
      }
    } catch (err) {
      errorDev("Operation failed:", err);
      return "";
    }

    return "";
  }, [avatarUrl]);

  useEffect(() => {
    let cancelled = false;

    setImageLoaded(false);
    setImageFailed(false);

    if (!normalizedAvatarUrl) {
      setImageFailed(true);
      return () => {
        cancelled = true;
      };
    }

    const probe = new Image();
    probe.onload = () => {
      if (!cancelled) setImageLoaded(true);
    };
    probe.onerror = () => {
      if (!cancelled) setImageFailed(true);
    };
    probe.src = normalizedAvatarUrl;

    return () => {
      cancelled = true;
    };
  }, [normalizedAvatarUrl]);

  if (normalizedAvatarUrl && imageLoaded && !imageFailed) {
    return (
      <img
        src={normalizedAvatarUrl}
        className={`${dimensions} rounded-full object-cover ${className}`}
        onError={() => setImageFailed(true)}
        alt={alt}
      />
    );
  }

  return (
    <div
      className={`${dimensions} rounded-full bg-green-200 text-green-900 flex items-center justify-center font-bold uppercase ${className}`}
      aria-hidden="true"
    >
      {getInitials(displayName, email)}
    </div>
  );
}
